import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  Timestamp,
  updateDoc,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { db } from "@/firebaseConfig";

export interface UserActivityEvent {
  id?: string;
  userId: string;
  activityType: "login" | "app_open" | "logout";
  timestamp: Timestamp;
  sessionId?: string;
  deviceInfo?: {
    userAgent: string;
    platform: string;
    isMobile: boolean;
  };
  metadata?: Record<string, any>;
}

export interface UserActivitySummary {
  userId: string;
  totalLogins: number;
  loginsThisWeek: number;
  loginsThisMonth: number;
  lastActivity: Date | null;
  averageSessionDuration?: number; // in minutes
  deviceTypes: Record<string, number>; // device type -> count
  activityPattern: {
    weeklyLogins: number[];
    hourlyDistribution: number[];
  };
}

export interface WeeklyActivityStats {
  week: string; // ISO week format (YYYY-Www)
  totalUsers: number;
  activeUsers: number;
  totalLogins: number;
  avgLoginsPerUser: number;
  newUsers: number;
}

class UserActivityService {
  private readonly ACTIVITY_COLLECTION = "user_activity_events";
  private readonly ACTIVITY_SUMMARY_COLLECTION = "user_activity_summary";

  /**
   * Track a user activity event (login, app open, etc.)
   */
  async trackActivity(
    userId: string,
    activityType: UserActivityEvent["activityType"],
    sessionId?: string,
    metadata?: Record<string, any>
  ): Promise<boolean> {
    try {
      const deviceInfo = this.getDeviceInfo();

      // Generate session ID if not provided and this is a login
      const finalSessionId =
        sessionId ||
        (activityType === "login" ? this.generateSessionId() : undefined);

      const activityEvent: Omit<UserActivityEvent, "id"> = {
        userId,
        activityType,
        timestamp: Timestamp.now(),
        deviceInfo,
        // Only include sessionId and metadata if they have values
        ...(finalSessionId && { sessionId: finalSessionId }),
        ...(metadata && Object.keys(metadata).length > 0 && { metadata }),
      };

      // Stamp the profile FIRST — this is the field the staff Users list reads, and it
      // used to run last, so any failure in the event write below silently starved it
      // (the "last active stuck at migration day" bug).
      try {
        await this.updateUserLastActivity(userId);
      } catch {
        // best-effort
      }

      // Use addDoc which generates a unique ID - handle race conditions gracefully
      try {
      await addDoc(collection(db, this.ACTIVITY_COLLECTION), activityEvent);
      } catch (addError: any) {
        // If document already exists (shouldn't happen with addDoc, but handle gracefully)
        if (addError?.code === 'already-exists' || addError?.message?.includes('already exists')) {
          // Continue with other updates even if this fails
        } else {
          throw addError; // Re-throw other errors
        }
      }

      // Update user activity summary (non-blocking)
      try {
      await this.updateUserActivitySummary(userId, activityType);
      } catch (summaryError) {
      }

      // Update user's lastActivity in their profile (non-blocking)
      try {
      await this.updateUserLastActivity(userId);
      } catch (activityError) {
      }

      return true;
    } catch (error) {
      console.error("Error tracking user activity:", error);
      return false;
    }
  }

  /**
   * Get user activity summary for a specific user
   */
  async getUserActivitySummary(
    userId: string
  ): Promise<UserActivitySummary | null> {
    try {
      const summaryRef = doc(db, this.ACTIVITY_SUMMARY_COLLECTION, userId);
      const summaryDoc = await getDoc(summaryRef);

      if (!summaryDoc.exists()) {
        // Generate summary from raw events if not exists
        return await this.generateUserActivitySummary(userId);
      }

      const data = summaryDoc.data();
      return {
        userId,
        totalLogins: data.totalLogins || 0,
        loginsThisWeek: data.loginsThisWeek || 0,
        loginsThisMonth: data.loginsThisMonth || 0,
        lastActivity: data.lastActivity?.toDate() || null,
        averageSessionDuration: data.averageSessionDuration || 0,
        deviceTypes: data.deviceTypes || {},
        activityPattern: data.activityPattern || {
          weeklyLogins: [],
          hourlyDistribution: [],
        },
      };
    } catch (error) {
      console.error("Error getting user activity summary:", error);
      return null;
    }
  }

  /**
   * Get weekly activity stats for the staff overview
   */
  async getWeeklyActivityStats(): Promise<WeeklyActivityStats[]> {
    try {
      const now = new Date();
      const weeks: WeeklyActivityStats[] = [];

      // Get last 8 weeks of data
      for (let i = 0; i < 8; i++) {
        const weekStart = new Date(now.getTime() - i * 7 * 24 * 60 * 60 * 1000);
        const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);

        const weekString = this.getISOWeek(weekStart);

        const weekQuery = query(
          collection(db, this.ACTIVITY_COLLECTION),
          where("timestamp", ">=", Timestamp.fromDate(weekStart)),
          where("timestamp", "<", Timestamp.fromDate(weekEnd)),
          where("activityType", "==", "login")
        );

        const weekSnapshot = await getDocs(weekQuery);
        const uniqueUsers = new Set<string>();

        weekSnapshot.docs.forEach((doc) => {
          uniqueUsers.add(doc.data().userId);
        });

        weeks.unshift({
          week: weekString,
          totalUsers: 0, // Will be calculated separately
          activeUsers: uniqueUsers.size,
          totalLogins: weekSnapshot.size,
          avgLoginsPerUser:
            uniqueUsers.size > 0 ? weekSnapshot.size / uniqueUsers.size : 0,
          newUsers: 0, // Will be calculated separately
        });
      }

      return weeks;
    } catch (error) {
      console.error("Error getting weekly activity stats:", error);
      return [];
    }
  }

  /**
   * Get monthly activity stats for the staff overview
   */
  async getMonthlyActivityStats(): Promise<
    Array<{
      month: string; // Format: "YYYY-MM"
      totalUsers: number;
      activeUsers: number;
      totalLogins: number;
      avgLoginsPerUser: number;
      newUsers: number;
    }>
  > {
    try {
      const now = new Date();
      const months: Array<{
        month: string;
        totalUsers: number;
        activeUsers: number;
        totalLogins: number;
        avgLoginsPerUser: number;
        newUsers: number;
      }> = [];

      // Start from this month and go forward up to 12 months (but only show months with data)
      // We'll check the last 3 months including current month to see if there's any data
      for (let i = 0; i <= 2; i++) {
        const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const nextMonthDate = new Date(
          now.getFullYear(),
          now.getMonth() - i + 1,
          1
        );

        const monthString = `${monthDate.getFullYear()}-${String(
          monthDate.getMonth() + 1
        ).padStart(2, "0")}`;

        const monthQuery = query(
          collection(db, this.ACTIVITY_COLLECTION),
          where("timestamp", ">=", Timestamp.fromDate(monthDate)),
          where("timestamp", "<", Timestamp.fromDate(nextMonthDate)),
          where("activityType", "==", "login")
        );

        const monthSnapshot = await getDocs(monthQuery);

        // Only include months that have actual activity data
        if (monthSnapshot.size > 0) {
          const uniqueUsers = new Set<string>();

          monthSnapshot.docs.forEach((doc) => {
            uniqueUsers.add(doc.data().userId);
          });

          months.unshift({
            month: monthString,
            totalUsers: 0, // Will be calculated separately if needed
            activeUsers: uniqueUsers.size,
            totalLogins: monthSnapshot.size,
            avgLoginsPerUser:
              uniqueUsers.size > 0 ? monthSnapshot.size / uniqueUsers.size : 0,
            newUsers: 0, // Will be calculated separately if needed
          });
        }
      }

      return months;
    } catch (error) {
      console.error("Error getting monthly activity stats:", error);
      return [];
    }
  }

  /**
   * Get activity stats for all users (for staff overview)
   */
  async getAllUsersActivityStats(): Promise<{
    totalUsers: number;
    activeThisWeek: number;
    activeThisMonth: number;
    averageLoginsPerWeek: number;
    topActiveUsers: Array<{
      userId: string;
      userName: string;
      loginsThisWeek: number;
      lastActivity: Date | null;
    }>;
  }> {
    try {
      const now = new Date();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      // Get weekly activity
      const weeklyQuery = query(
        collection(db, this.ACTIVITY_COLLECTION),
        where("timestamp", ">=", Timestamp.fromDate(oneWeekAgo)),
        where("activityType", "==", "login")
      );

      // Get monthly activity
      const monthlyQuery = query(
        collection(db, this.ACTIVITY_COLLECTION),
        where("timestamp", ">=", Timestamp.fromDate(oneMonthAgo)),
        where("activityType", "==", "login")
      );

      const [weeklySnapshot, monthlySnapshot] = await Promise.all([
        getDocs(weeklyQuery),
        getDocs(monthlyQuery),
      ]);

      const weeklyActiveUsers = new Set<string>();
      const monthlyActiveUsers = new Set<string>();
      const userLoginCounts: Record<string, number> = {};

      weeklySnapshot.docs.forEach((doc) => {
        const userId = doc.data().userId;
        weeklyActiveUsers.add(userId);
        userLoginCounts[userId] = (userLoginCounts[userId] || 0) + 1;
      });

      monthlySnapshot.docs.forEach((doc) => {
        monthlyActiveUsers.add(doc.data().userId);
      });

      // Get top active users
      const topActiveUsers = Object.entries(userLoginCounts)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10)
        .map(([userId, count]) => ({
          userId,
          userName: `User ${userId.substring(0, 8)}`, // Placeholder - you can enhance this
          loginsThisWeek: count,
          lastActivity: null, // Can be enhanced
        }));

      const totalLogins = weeklySnapshot.size;
      const averageLoginsPerWeek =
        weeklyActiveUsers.size > 0 ? totalLogins / weeklyActiveUsers.size : 0;

      return {
        totalUsers: 0, // This should come from users collection
        activeThisWeek: weeklyActiveUsers.size,
        activeThisMonth: monthlyActiveUsers.size,
        averageLoginsPerWeek,
        topActiveUsers,
      };
    } catch (error) {
      console.error("Error getting all users activity stats:", error);
      return {
        totalUsers: 0,
        activeThisWeek: 0,
        activeThisMonth: 0,
        averageLoginsPerWeek: 0,
        topActiveUsers: [],
      };
    }
  }

  /**
   * Get detailed activity data for all users (for staff user activity tab)
   */
  async getAllUsersWithActivityDetails(): Promise<
    Array<{
      userId: string;
      userName: string;
      firstName?: string;
      lastName?: string;
      email: string;
      lastActivity: Timestamp | null;
      loginsThisWeek: number;
      loginsThisMonth: number;
      totalLogins: number;
      deviceTypes: Record<string, number>;
      isActive: boolean;
    }>
  > {
    try {
      // Get all users from users collection
      const usersQuery = query(collection(db, "users"));
      const usersSnapshot = await getDocs(usersQuery);

      // Get all activity summaries
      const summariesQuery = query(
        collection(db, this.ACTIVITY_SUMMARY_COLLECTION)
      );
      const summariesSnapshot = await getDocs(summariesQuery);

      // Create a map of activity summaries by userId
      const activityMap = new Map<string, any>();
      summariesSnapshot.docs.forEach((doc) => {
        activityMap.set(doc.id, doc.data());
      });

      // Combine user data with activity data
      const usersWithActivity = usersSnapshot.docs.map((userDoc) => {
        const userData = userDoc.data();
        const activityData = activityMap.get(userDoc.id) || {};

        const lastActivity =
          activityData.lastActivity || userData.lastActivity || null;
        const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        const isActive = lastActivity
          ? lastActivity.toDate() > oneWeekAgo
          : false;

        return {
          userId: userDoc.id,
          userName:
            `${userData.firstName || ""} ${userData.lastName || ""}`.trim() ||
            userData.email ||
            "Unknown User",
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email || "No email",
          lastActivity: lastActivity,
          loginsThisWeek: activityData.loginsThisWeek || 0,
          loginsThisMonth: activityData.loginsThisMonth || 0,
          totalLogins: activityData.totalLogins || 0,
          deviceTypes: activityData.deviceTypes || {},
          isActive,
        };
      });

      return usersWithActivity;
    } catch (error) {
      console.error("Error getting all users with activity details:", error);
      return [];
    }
  }

  /**
   * Private helper methods
   */
  private getDeviceInfo() {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        userAgent
      );

    return {
      userAgent,
      platform,
      isMobile,
    };
  }

  private async updateUserActivitySummary(
    userId: string,
    activityType: UserActivityEvent["activityType"]
  ): Promise<void> {
    try {
      const summaryRef = doc(db, this.ACTIVITY_SUMMARY_COLLECTION, userId);
      const summaryDoc = await getDoc(summaryRef);

      const now = new Date();
      const weekStart = this.getWeekStart(now);
      const monthStart = this.getMonthStart(now);

      if (!summaryDoc.exists()) {
        // Create new summary
        await setDoc(summaryRef, {
          userId,
          totalLogins: activityType === "login" ? 1 : 0,
          loginsThisWeek: activityType === "login" ? 1 : 0,
          loginsThisMonth: activityType === "login" ? 1 : 0,
          lastActivity: Timestamp.now(),
          weekStart: Timestamp.fromDate(weekStart),
          monthStart: Timestamp.fromDate(monthStart),
          deviceTypes: {},
          activityPattern: {
            weeklyLogins: [],
            hourlyDistribution: Array(24).fill(0),
          },
        });
      } else {
        // Update existing summary
        const data = summaryDoc.data();
        const updates: any = {
          lastActivity: Timestamp.now(),
        };

        if (activityType === "login") {
          updates.totalLogins = (data.totalLogins || 0) + 1;

          // Check if we need to reset weekly/monthly counters
          const lastWeekStart = data.weekStart?.toDate();
          const lastMonthStart = data.monthStart?.toDate();

          if (!lastWeekStart || lastWeekStart < weekStart) {
            updates.loginsThisWeek = 1;
            updates.weekStart = Timestamp.fromDate(weekStart);
          } else {
            updates.loginsThisWeek = (data.loginsThisWeek || 0) + 1;
          }

          if (!lastMonthStart || lastMonthStart < monthStart) {
            updates.loginsThisMonth = 1;
            updates.monthStart = Timestamp.fromDate(monthStart);
          } else {
            updates.loginsThisMonth = (data.loginsThisMonth || 0) + 1;
          }

          // Update hourly distribution
          const hour = now.getHours();
          const hourlyDist =
            data.activityPattern?.hourlyDistribution || Array(24).fill(0);
          hourlyDist[hour] = (hourlyDist[hour] || 0) + 1;
          updates[`activityPattern.hourlyDistribution`] = hourlyDist;
        }

        await updateDoc(summaryRef, updates);
      }
    } catch (error) {
      console.error("Error updating user activity summary:", error);
    }
  }

  private async updateUserLastActivity(userId: string): Promise<void> {
    try {
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, {
        lastActivity: Timestamp.now(),
      });
    } catch (error) {
      console.error("Error updating user last activity:", error);
      // Don't throw - this is supplementary
    }
  }

  private async generateUserActivitySummary(
    userId: string
  ): Promise<UserActivitySummary | null> {
    try {
      const userQuery = query(
        collection(db, this.ACTIVITY_COLLECTION),
        where("userId", "==", userId),
        orderBy("timestamp", "desc"),
        limit(100)
      );

      const userSnapshot = await getDocs(userQuery);

      if (userSnapshot.empty) {
        return null;
      }

      const now = new Date();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      let totalLogins = 0;
      let loginsThisWeek = 0;
      let loginsThisMonth = 0;
      let lastActivity: Date | null = null;
      const deviceTypes: Record<string, number> = {};

      userSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        const timestamp = data.timestamp.toDate();

        if (data.activityType === "login") {
          totalLogins++;

          if (timestamp > oneWeekAgo) {
            loginsThisWeek++;
          }

          if (timestamp > oneMonthAgo) {
            loginsThisMonth++;
          }
        }

        if (!lastActivity || timestamp > lastActivity) {
          lastActivity = timestamp;
        }

        // Track device types
        if (data.deviceInfo?.platform) {
          const platform = data.deviceInfo.platform;
          deviceTypes[platform] = (deviceTypes[platform] || 0) + 1;
        }
      });

      return {
        userId,
        totalLogins,
        loginsThisWeek,
        loginsThisMonth,
        lastActivity,
        deviceTypes,
        activityPattern: {
          weeklyLogins: [],
          hourlyDistribution: [],
        },
      };
    } catch (error) {
      console.error("Error generating user activity summary:", error);
      return null;
    }
  }

  private getWeekStart(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff));
  }

  private getMonthStart(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  private getISOWeek(date: Date): string {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
    const week1 = new Date(d.getFullYear(), 0, 4);
    const weekNum =
      1 +
      Math.round(
        ((d.getTime() - week1.getTime()) / 86400000 -
          3 +
          ((week1.getDay() + 6) % 7)) /
          7
      );
    return `${d.getFullYear()}-W${weekNum.toString().padStart(2, "0")}`;
  }

  private generateSessionId(): string {
    // Generate a session ID based on timestamp and random string
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substring(2, 8);
    return `${timestamp}-${randomStr}`;
  }
}

export const userActivityService = new UserActivityService();
